import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (this.dataset.collectionConfirm === 'true' && productCard?.productPageUrl) {
      return new URL(productCard.productPageUrl, window.location.origin).searchParams.get('variant');
    }
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    // A single-option card can resolve to an unavailable variant (e.g. re-selecting a
    // sold-out swatch). Keep "Choose" so shoppers reach the picker, not a dead-end disabled "Add".
    if (quickAddButton === 'add' && this.#isSelectedVariantUnavailable()) {
      quickAddButton = 'choose';
    }

    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Whether the card's currently selected swatch maps to an unavailable variant.
   * Reads `data-option-available` off the variant picker's selected option - the same
   * signal the product card uses. Only reports true on an explicit `false`, so an
   * unknown/absent signal leaves the caller's default ("add") untouched.
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;
    if (this.dataset.collectionConfirm === 'true') {
      const dialogComponent = document.getElementById('quick-add-dialog');
      dialogComponent?.setAttribute('data-collection-confirm', 'true');
    } else {
      document.getElementById('quick-add-dialog')?.removeAttribute('data-collection-confirm');
    }

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    if (!productGrid && this.dataset.collectionConfirm === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }
    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (this.dataset.collectionConfirm === 'true') {
      const card = this.closest('product-card');
      const media = productGrid.querySelector('.product-information__media');
      const price = productGrid.querySelector('product-price');
      const inventory = productGrid.querySelector('product-inventory');
      const picker = productGrid.querySelector('variant-picker');
      const purchase = productGrid.querySelector('.buy-buttons-block') || productGrid.querySelector('product-form-component');
      const galleryColumn = document.createElement('div');
      galleryColumn.className = 'collection-quick-panel__gallery';
      if (media) {
        const selectedId = new URL(this.productPageUrl, window.location.origin).searchParams.get('variant');
        const selectedThumb = selectedId ? card?.querySelector(`collection-variant-thumbnails button[data-variant-id="${selectedId}"] img`) : null;
        const pictures = Array.from(media.querySelectorAll('img')).filter((image) => image.getAttribute('src'));
        const initialImage = selectedThumb || pictures[0];
        if (initialImage) {
          const hero = document.createElement('img');
          hero.className = 'collection-quick-panel__image';
          hero.alt = this.dataset.productTitle || '';
          const useImage = (source) => {
            const url = new URL(source.getAttribute('src'), window.location.origin);
            if (url.hostname === 'cdn.shopify.com' || url.pathname.includes('/cdn/shop/')) url.searchParams.set('width', '900');
            hero.src = url.toString();
          };
          useImage(initialImage);
          galleryColumn.append(hero);
          const strip = document.createElement('div');
          strip.className = 'collection-quick-panel__photos';
          const seen = new Set();
          for (const picture of pictures) {
            const key = new URL(picture.getAttribute('src'), window.location.origin).pathname;
            if (seen.has(key)) continue;
            seen.add(key);
            const photo = document.createElement('button');
            photo.type = 'button';
            photo.setAttribute('aria-label', picture.alt || this.dataset.productTitle || '');
            photo.append(picture.cloneNode(true));
            photo.dataset.panelImageSrc = picture.getAttribute('src');
            strip.append(photo);
          }
          galleryColumn.append(strip);
        }
      }
      const detailsColumn = document.createElement('div');
      detailsColumn.className = 'collection-quick-panel__details';
      const title = document.createElement('a');
      title.className = 'collection-quick-panel__title';
      title.href = this.productPageUrl;
      title.textContent = this.dataset.productTitle || '';
      detailsColumn.append(title);
      const social = document.createElement('div');
      social.className = 'collection-quick-panel__social';
      for (const selector of ['.collection-modern-rating', '.collection-book-distinction']) {
        const source = card?.querySelector(selector);
        if (source) social.append(source.cloneNode(true));
      }
      if (social.childElementCount) detailsColumn.append(social);
      if (price) detailsColumn.append(price);
      if (inventory) detailsColumn.append(inventory);
      const campaign = card?.querySelector('.collection-campaign-badge');
      if (campaign) galleryColumn.prepend(campaign.cloneNode(true));
      const delivery = card?.querySelector('.collection-delivery-note');
      const reassurance = document.createElement('div');
      reassurance.className = 'collection-quick-panel__reassurance';
      if (delivery) reassurance.append(delivery.cloneNode(true));
      const choices = document.createElement('div');
      choices.className = 'collection-quick-panel__choices';
      if (picker) {
        choices.append(picker);
      } else {
        // No native picker in the product template: expose the full variants
        // already rendered by Liquid on the collection card, not guessed options.
        const sourceButtons = card?.querySelectorAll('collection-variant-thumbnails button[data-variant-id]');
        const currentVariant = new URL(this.productPageUrl, window.location.origin).searchParams.get('variant');
        for (const source of sourceButtons || []) {
          const choice = document.createElement('button');
          choice.type = 'button';
          choice.className = 'collection-quick-panel__variant';
          choice.dataset.panelVariantId = source.dataset.variantId;
          choice.setAttribute('aria-pressed', String(source.dataset.variantId === currentVariant || (!currentVariant && source.getAttribute('aria-pressed') === 'true')));
          choice.setAttribute('aria-label', source.getAttribute('aria-label') || source.dataset.variantTitle || '');
          const image = source.querySelector('img');
          if (image) choice.append(image.cloneNode(true));
          const name = document.createElement('span');
          name.textContent = source.dataset.variantTitle || '';
          choice.append(name);
          choices.append(choice);
        }
      }
      detailsColumn.append(choices);
      if (reassurance.childElementCount) detailsColumn.append(reassurance);
      if (purchase) detailsColumn.append(purchase);
      productGrid.replaceChildren(galleryColumn, detailsColumn);
    } else if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    if (this.dataset.collectionConfirm === 'true') {
      // Match the actual collection-card typography, while retaining the server-rendered variant price.
      const cardPrice = this.closest('product-card')?.querySelector('product-price');
      for (const [selector, prefix] of [['.price', '--cq-price'], ['.compare-at-price', '--cq-compare']]) {
        const source = cardPrice?.querySelector(selector);
        if (!source) continue;
        const computed = getComputedStyle(source);
        for (const property of ['font-family', 'font-size', 'font-weight', 'line-height', 'letter-spacing', 'color']) {
          modalContent.style.setProperty(`${prefix}-${property}`, computed.getPropertyValue(property));
        }
      }
      for (const photo of modalContent.querySelectorAll('button[data-panel-image-src]')) {
        photo.onclick = () => {
          const hero = modalContent.querySelector('.collection-quick-panel__image');
          if (!hero || !photo.dataset.panelImageSrc) return;
          const url = new URL(photo.dataset.panelImageSrc, window.location.origin);
          if (url.hostname === 'cdn.shopify.com' || url.pathname.includes('/cdn/shop/')) url.searchParams.set('width', '900');
          hero.src = url.toString();
        };
      }
      for (const button of modalContent.querySelectorAll('button[data-panel-variant-id]')) {
        button.onclick = async () => {
          const variantId = button.dataset.panelVariantId;
          const card = this.closest('product-card');
          if (!variantId || !card) return;
          const url = new URL(this.productPageUrl, window.location.origin);
          url.searchParams.set('variant', variantId);
          const buttons = modalContent.querySelectorAll('button[data-panel-variant-id]');
          const purchaseButtons = modalContent.querySelectorAll('product-form-component button[type="submit"]');
          const states = Array.from(purchaseButtons, (element) => [element, element.disabled]);
          buttons.forEach((element) => { element.disabled = true; });
          purchaseButtons.forEach((element) => { element.disabled = true; });
          modalContent.setAttribute('aria-busy', 'true');
          try {
            const html = await this.fetchProductPage(url.toString());
            const grid = html?.querySelector('[data-product-grid-content]');
            if (!grid) throw new Error('Variant panel content unavailable');
            card.applyVariantToLinks?.(variantId);
            card.updatePrice?.(html);
            const thumbnail = card.querySelector(`collection-variant-thumbnails button[data-variant-id="${variantId}"]`);
            if (thumbnail) {
              card.querySelectorAll('collection-variant-thumbnails button[data-variant-id]').forEach((element) => element.setAttribute('aria-pressed', String(element === thumbnail)));
            }
            await this.updateQuickAddModal(grid.cloneNode(true));
          } catch (error) {
            if (error?.name !== 'AbortError') {
              // Server-rendered product page is the safe fallback, never submit stale variant data.
              window.location.href = url.toString();
            }
          } finally {
            modalContent.removeAttribute('aria-busy');
            buttons.forEach((element) => { element.disabled = false; });
            states.forEach(([element, disabled]) => { if (element.isConnected) element.disabled = disabled; });
          }
        };
      }
    }
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
